import Bugsnag from '@bugsnag/js';
import { Dialog, DialogBody, DialogContent, type SelectOption } from '@civilgrid/ui/components';
import { type UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  deleteGeoItem as apiDeleteGeoItem,
  editGeoItem as apiEditGeoItem,
  listGeoItemsV2 as apiListGeoItems,
} from '~/api/projects2';
import qrCode from '~/assets/img/civilgrid-app-qr-code.png';
import videoPlaceholder from '~/assets/img/video-placeholder.jpeg';
import type { GeoItem } from '~/common/types';
import { Notify } from '~/components2/Notify';

function getItem(value: string): SelectOption {
  return { label: value, value };
}

export const weatherConditionItems = [getItem('Wet'), getItem('Dry')];
export const siteChoiceItems = [getItem('Primary'), getItem('Alternate')];
export const yesNoItems = [getItem('Yes'), getItem('No')];
export const yesNoMaybeItems = [...yesNoItems, getItem('Maybe')];
export const surchargeItems = [
  getItem('No evidence visible'),
  getItem('Remains in pipe'),
  getItem('Reaches rim'),
  getItem('Evidence unclear'),
];
export const siteAccessItems = [
  getItem('Good'),
  getItem('Fair'),
  getItem('Poor'),
  getItem('Traffic Control Only'),
  getItem('Unusable'),
];
export const structuralIntegrityItems = [getItem('Good'), getItem('Fair'), getItem('Poor')];
export const infEffItems = [getItem('Influent'), getItem('Effluent'), getItem('Manhole'), getItem('None')];
export const priorityItems = [getItem('High'), getItem('Medium'), getItem('Low')];

export const surfaceTypeItems = [getItem('Asphalt'), getItem('Concrete'), getItem('Landscape'), getItem('Dirt')];
export const soilItems = [getItem('Dirt'), getItem('Hard Dirt'), getItem('Road Base'), getItem('Rocks')];
export const materialItems = [
  getItem('AC'),
  getItem('Copper'),
  getItem('Metal'),
  getItem('Plastic'),
  getItem('Poly'),
  getItem('PVC (white)'),
  getItem('PVC (rigid)'),
];

export function FormSection({ title, children }: { title: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-base">{title}</div>
      {children}
    </div>
  );
}

export function ReadOnlyField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm">{value ?? ''}</span>
    </div>
  );
}

export function useListGeoItems({ projectId, enabled = true }: { projectId: string; enabled?: boolean }) {
  return useQuery({
    refetchOnWindowFocus: false,
    enabled,
    queryKey: ['list-geo-items', { projectId }],
    queryFn: () => apiListGeoItems(projectId),
  });
}

export { filterGeoItemsByReportType } from './filterGeoItemsByReportType';

export function useInvalidateGeoItems({ projectId }: { projectId: string }) {
  const q = useQueryClient();
  const invalidate = () => {
    q.invalidateQueries({
      queryKey: ['list-geo-items', { projectId }],
    });
  };

  return invalidate;
}

/** Logs to the console and reports to Bugsnag so geo-item mutation failures are tracked. */
function reportGeoItemError(message: string, error: unknown) {
  console.error(message, error);
  Bugsnag.notify(error instanceof Error ? error : new Error(`${message}: ${String(error)}`));
}

/**
 * The geo item's `version` when its edit session opened, held until a different item is passed.
 * A refetch must not move it: the server records anything newer as changes this editor never saw.
 */
export function useEditBaseVersion(geoItem: Pick<GeoItem, 'id' | 'version'>): number | undefined {
  const [base, setBase] = useState({ id: geoItem.id, version: geoItem.version });
  if (base.id !== geoItem.id) {
    setBase({ id: geoItem.id, version: geoItem.version });
    return geoItem.version;
  }
  return base.version;
}

// biome-ignore lint/suspicious/noConfusingVoidType: void is correct here — mutation takes no variables
type OnDeleteSuccess = ((data: any, variables: void, context: unknown) => Promise<unknown> | unknown) | undefined;

export function useDeleteGeoItem({
  projectId,
  geoItemId,
  baseVersion,
  onSuccess,
  onError,
}: {
  projectId: string;
  geoItemId: string;
  /** From {@link useEditBaseVersion}; without it the server can't check the save for overwrites. */
  baseVersion?: number;
  onSuccess?: OnDeleteSuccess;
  /** Replaces the default toast (error is always logged + reported first). */
  onError?: (error: unknown) => void;
}) {
  return useMutation({
    mutationKey: ['delete-geo-item', { projectId, geoItemId }],
    mutationFn: () => apiDeleteGeoItem(projectId, geoItemId, baseVersion),
    onSuccess,
    onError: (error) => {
      reportGeoItemError('Error deleting geo item', error);
      if (onError) {
        onError(error);
        return;
      }
      Notify.error({
        message: 'An error occurred while deleting your log, please contact support.',
      });
    },
  });
}

// make TS happy
type OnSuccess = ((data: any, variables: GeoItem['props'], context: unknown) => Promise<unknown> | unknown) | undefined;

export function useUpdateGeoItem({
  projectId,
  geoItemId,
  baseVersion,
  onSuccess,
  onError,
}: {
  projectId: string;
  geoItemId: string;
  /** From {@link useEditBaseVersion}; without it the server can't check the save for overwrites. */
  baseVersion?: number;
  onSuccess?: OnSuccess;
  /** Replaces the default toast (error is always logged + reported first). */
  onError?: (error: unknown) => void;
}) {
  return useMutation({
    mutationKey: ['edit-geo-item', { projectId, geoItemId }],
    mutationFn: (props: GeoItem['props']) => apiEditGeoItem(projectId, geoItemId, { props, baseVersion }),
    onSuccess,
    onError: (error) => {
      reportGeoItemError('Error updating geo item', error);
      if (onError) {
        onError(error);
        return;
      }
      Notify.error({
        message: 'An error occurred while updating your log, please contact support.',
      });
    },
  });
}

export function getIsLoading(queries: UseQueryResult[]) {
  return queries.some((q) => !!q.isFetching);
}

export function getIsError(queries: UseQueryResult[]) {
  return queries.some((q) => !!q.isError);
}

export const utilityTypes = [
  'Electric',
  'Fiber',
  'Gas',
  'Sewer',
  'Steam',
  'Storm',
  'Telco',
  'Water',
  'Unknown',
  'Other',
];

// Pure pothole prop helpers (form-value type, field maps, and the load/save
// prop builder) live in `./potholeProps` so they stay testable without pulling
// in this file's React/UI imports. Re-exported here for existing import paths.
export {
  buildPotholeUpdateProps,
  LONGITUDE_LATITUDE_PROP_LABEL,
  potholeFieldConfig,
  potholeFieldMap,
  type TPotholeFormValues,
} from './potholeProps';

export type TManholeFormValues = {
  // general
  manholeNumber: string;
  siteChoice: string;
  crewMembers: string;
  weatherConditions: string;
  addressLocation: string;

  // hydraulic conditions (influent)
  velocity: string;
  depth: string;

  // sewer
  heightInf1: string;
  widthInf1: string;
  materialInf1: string;
  shapeInf1: string;
  heightInf2: string;
  widthInf2: string;
  materialInf2: string;
  shapeInf2: string;
  heightEff: string;
  widthEff: string;
  materialEff: string;
  shapeEff: string;

  // sediment
  sedimentPresent: string;
  hardPacked: string;
  softPacked: string;

  // surcharge
  surcharge: string;
  distanceFromRim: string;

  // gas
  O2level: string;

  // site conditions
  siteAccess: string;

  // manhole information
  elevatedManhole: string;
  boltedManhole: string;
  heightAboveGround: string;
  manholeDepth: string;
  pipeOffset: string;
  structureIntegrity: string;

  // pipe condition
  pipeBends: string;
  distanceToBend: string;
  sizeGeomMaterialChange: string;
  distanceToChange: string;
  changeDetails: string;

  // crew observations
  canMaintainSite: string;
  siteComments: string;

  // sensor config
  levelPrimary: string;
  levelRedundant: string;
  depthPrimary: string;
  depthRedundant: string;
  installComments: string;
};

export const manholeFieldMap: { [key in keyof TManholeFormValues]: string } = {
  manholeNumber: 'MH#',
  siteChoice: 'Site Choice',
  crewMembers: 'Crew Members',
  weatherConditions: 'Weather Conditions',
  addressLocation: 'Address/Location',

  velocity: 'Velocity',
  depth: 'Depth',

  heightInf1: 'Height (Inf1)',
  widthInf1: 'Width (Inf1)',
  materialInf1: 'Material (Inf1)',
  shapeInf1: 'Shape (Inf1)',
  heightInf2: 'Height (Inf2)',
  widthInf2: 'Width (Inf2)',
  materialInf2: 'Material (Inf2)',
  shapeInf2: 'Shape (Inf2)',
  heightEff: 'Height (Eff)',
  widthEff: 'Width (Eff)',
  materialEff: 'Material (Eff)',
  shapeEff: 'Shape (Eff)',

  sedimentPresent: 'Sediment Present',
  hardPacked: 'Hard Packed',
  softPacked: 'Soft Packed',

  surcharge: 'Surcharge',
  distanceFromRim: 'Distance from rim',

  O2level: 'O2 Level',

  siteAccess: 'Site Access',

  elevatedManhole: 'Elevated Manhole',
  boltedManhole: 'Bolted Manhole',
  heightAboveGround: 'Height above ground',
  manholeDepth: 'Manhole Depth',
  pipeOffset: 'Pipe offset',
  structureIntegrity: 'Structural Integrity',

  pipeBends: 'Pipe Bends',
  distanceToBend: 'Distance to bend',
  sizeGeomMaterialChange: 'Size/Geom/Material Change',
  distanceToChange: 'Distance to change',
  changeDetails: 'Change details',

  canMaintainSite: 'Can maintain site?',
  siteComments: 'Site Comments',

  levelPrimary: 'Level (Primary)',
  levelRedundant: 'Level (Redundant)',
  depthPrimary: 'Depth (Primary)',
  depthRedundant: 'Depth (Redundant)',
  installComments: 'Install Comments',
};

// `getInitialValues` lives in its own dependency-light module so it can be
// unit-tested in the node env without dragging in this file's UI imports.
export { getInitialValues } from './getInitialValues';

export function Label({ children }: { children?: React.ReactNode }) {
  return <div className="my-1 text-base">{children}</div>;
}

type GeoItemFile = { path: string; type: string; name?: string | null; thumbnailPath?: string | null };

type OpenFilePreview = (file: GeoItemFile, opener: HTMLElement) => void;

const FilePreviewContext = createContext<OpenFilePreview | null>(null);

/**
 * Hosts the attachment preview dialog beside `children`, so {@link FileList} thumbnails rendered inside another
 * dialog open it as a stacked sibling rather than a dialog nested in that dialog's content.
 */
export function GeoItemFilePreviewProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<GeoItemFile | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const openFilePreview = useCallback<OpenFilePreview>((nextFile, opener) => {
    openerRef.current = opener;
    setFile(nextFile);
    setOpen(true);
  }, []);

  const isVideo = file?.type.startsWith('video') ?? false;

  return (
    <FilePreviewContext.Provider value={openFilePreview}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        {file && (
          <DialogContent
            title={file.name || 'File preview'}
            className="z-[130] w-auto min-w-min max-w-[75vw]"
            overlayClassName="z-[120]"
            data-testid="pothole-attachment-preview-dialog"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              openerRef.current?.focus();
            }}
          >
            <DialogBody className="text-sm">
              <div className="relative flex max-h-[70vh] max-w-[70vw] justify-center overflow-hidden">
                {isVideo ? (
                  <video controls autoPlay src={file.path} className="max-h-full max-w-full object-contain">
                    <track kind="captions" />
                  </video>
                ) : (
                  <img
                    className="max-h-full max-w-full object-contain"
                    src={file.path}
                    alt={`Inspection attachment: ${file.name}`}
                  />
                )}
              </div>
            </DialogBody>
          </DialogContent>
        )}
      </Dialog>
    </FilePreviewContext.Provider>
  );
}

/** Attachment thumbnails; each opens the preview hosted by the nearest {@link GeoItemFilePreviewProvider}. */
export function FileList({ geoItem }: { geoItem: GeoItem }) {
  const openFilePreview = useContext(FilePreviewContext);

  if (!geoItem.files || !geoItem.files?.length) {
    return <div>No files for {geoItem.fields?.['MH#']?.value ?? geoItem.fields?.['PH#']?.value}</div>;
  }

  return (
    <div className="flex flex-wrap items-start justify-start gap-2">
      {(geoItem.files as GeoItemFile[]).map((file, index) => {
        const isVideo = file.type.startsWith('video');
        const thumbnailPath = isVideo ? videoPlaceholder : (file.thumbnailPath ?? file.path);
        const thumbnail = (
          <img
            className="h-28 w-28 object-cover rounded-md"
            src={thumbnailPath}
            alt={`Inspection attachment: ${file.name}`}
          />
        );

        if (!openFilePreview) {
          return (
            <div key={file.path} className="h-28 w-28">
              {thumbnail}
            </div>
          );
        }

        return (
          <button
            key={file.path}
            type="button"
            className="h-28 w-28 cursor-pointer rounded-md border-none bg-transparent p-0 focus-ring"
            aria-label={`Preview ${file.name || (isVideo ? 'video' : 'image')}`}
            aria-haspopup="dialog"
            data-testid={`pothole-attachment-thumbnail-${index}`}
            onClick={(event) => openFilePreview(file, event.currentTarget)}
          >
            {thumbnail}
          </button>
        );
      })}
    </div>
  );
}

export const manholeFieldConfig: {
  [field: string]: 'string' | 'select' | 'date' | 'number';
} = {
  'MH#': 'string',
  'Site Choice': 'select',
  'Crew Members': 'string',
  'Weather Conditions': 'select',
  'Address/Location': 'string',
  Velocity: 'number',
  Depth: 'number',
  'Height (Inf1)': 'number',
  'Width (Inf1)': 'number',
  'Material (Inf1)': 'string',
  'Shape (Inf1)': 'string',
  'Height (Inf2)': 'number',
  'Width (Inf2)': 'number',
  'Material (Inf2)': 'string',
  'Shape (Inf2)': 'string',
  'Height (Eff)': 'number',
  'Width (Eff)': 'number',
  'Material (Eff)': 'string',
  'Shape (Eff)': 'string',
  'Sediment Present': 'select',
  'Hard Packed': 'number',
  'Soft Packed': 'number',
  Surcharge: 'select',
  'Distance from rim': 'number',
  'O2 Level': 'number',
  'Site Access': 'select',
  'Elevated Manhole': 'select',
  'Bolted Manhole': 'select',
  'Height above ground': 'number',
  'Manhole Depth': 'number',
  'Pipe offset': 'number',
  'Structural Integrity': 'select',
  'Pipe Bends': 'select',
  'Distance to bend': 'number',
  'Size/Geom/Material Change': 'select',
  'Distance to change': 'number',
  'Change details': 'string',
  'Can maintain site?': 'select',
  'Site Comments': 'string',
  'Level (Primary)': 'string',
  'Level (Redundant)': 'string',
  'Depth (Primary)': 'string',
  'Depth (Redundant)': 'string',
  'Install Comments': 'string',
  Date: 'date',
};

import { APP_STORE_URL, PLAY_STORE_URL } from '~/constants/links';

export function CivilGridMobileCode({
  message = 'Use the CivilGrid mobile app to capture potholes in the field that can be reviewed here!',
}: {
  message?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center space-y-8 text-center text-xl">
      <div className="mt-40">{message}</div>
      <div>
        Scan the QR Code or click{' '}
        <a
          target="_blank"
          href="https://map.civilgrid.com/download"
          rel="noreferrer noopener"
          className="text-blue-50 underline hover:text-blue-60"
        >
          here
        </a>{' '}
        to download CivilGrid Mobile
      </div>
      <div className="h-40 w-40 bg-cover" style={{ backgroundImage: `url(${qrCode})` }} />
      <div className="flex items-center gap-4">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-50 underline hover:text-blue-60"
        >
          <img
            src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
            alt="Download on the App Store"
            style={{ height: 54 }}
          />
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-50 underline hover:text-blue-60"
        >
          <img
            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
            alt="Get it on Google Play"
            style={{ height: 80, margin: '-13px 0' }}
          />
        </a>
      </div>
    </div>
  );
}
